import React, { useEffect, useState } from 'react';
import api from '../api';
import theme from '../theme';
import { Card, Button, Input, Select, Spinner, Empty, StatusBadge, Money, ConfirmDialog } from '../components/common';

export default function StandingOrders() {
  const [orders, setOrders] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [actionId, setActionId] = useState(null);
  const [actionType, setActionType] = useState('');

  const [form, setForm] = useState({
    sourceAccountId: '', beneficiaryId: '', amount: '', reference: '',
    scheme: 'FPS', frequency: 'MONTHLY', startDate: '', currency: 'GBP',
  });

  const load = () => {
    Promise.all([
      api.get('/standing-orders'),
      api.get('/accounts'),
      api.get('/beneficiaries'),
    ]).then(([o, a, b]) => {
      setOrders(o);
      setAccounts(a.filter(acc => acc.status === 'ACTIVE'));
      setBeneficiaries(b.filter(bn => bn.status === 'ACTIVE'));
    }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.post('/standing-orders', {
        sourceAccountId: form.sourceAccountId,
        beneficiaryId: form.beneficiaryId,
        amount: parseFloat(form.amount) || 0,
        currency: form.currency,
        scheme: form.scheme,
        reference: form.reference,
        frequency: form.frequency,
        startDate: form.startDate,
      });
      setShowForm(false);
      setForm({ sourceAccountId: '', beneficiaryId: '', amount: '', reference: '', scheme: 'FPS', frequency: 'MONTHLY', startDate: '', currency: 'GBP' });
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAction = async () => {
    try {
      if (actionType === 'cancel') {
        await api.post(`/standing-orders/${actionId}/cancel`, { reason: 'Cancelled by customer' });
      } else {
        await api.post(`/standing-orders/${actionId}/${actionType}`, {});
      }
      setActionId(null);
      setActionType('');
      load();
    } catch (err) {
      setError(err.message);
      setActionId(null);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '60px' }}><Spinner size={32} /></div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ fontSize: '20px', margin: 0 }}>Standing Orders</h1>
        <Button variant="accent" onClick={() => setShowForm(true)}>New standing order</Button>
      </div>

      {error && (
        <Card style={{ background: '#FED7D7', borderColor: theme.colors.danger, marginBottom: '16px' }}>
          <div style={{ color: '#742A2A', fontSize: '14px' }}>{error}</div>
        </Card>
      )}

      {showForm && (
        <Card style={{ marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>New standing order</h3>
          <form onSubmit={handleSubmit}>
            <Select label="From account" value={form.sourceAccountId} onChange={e => set('sourceAccountId', e.target.value)}>
              <option value="">Select account...</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.accountNumber})</option>)}
            </Select>
            <Select label="To beneficiary" value={form.beneficiaryId} onChange={e => set('beneficiaryId', e.target.value)}>
              <option value="">Select beneficiary...</option>
              {beneficiaries.map(b => <option key={b.id} value={b.id}>{b.nickname || b.name}</option>)}
            </Select>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <Input label="Amount (GBP)" type="number" step="0.01" min="0.01" value={form.amount} onChange={e => set('amount', e.target.value)} />
              <Input label="Reference" value={form.reference} onChange={e => set('reference', e.target.value)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <Select label="Frequency" value={form.frequency} onChange={e => set('frequency', e.target.value)}>
                <option value="WEEKLY">Weekly</option>
                <option value="FORTNIGHTLY">Fortnightly</option>
                <option value="MONTHLY">Monthly</option>
                <option value="QUARTERLY">Quarterly</option>
                <option value="ANNUAL">Annual</option>
              </Select>
              <Input label="Start date" type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
              <Button variant="ghost" type="button" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button variant="primary" loading={saving} disabled={!form.sourceAccountId || !form.beneficiaryId || !form.amount || !form.reference || !form.startDate}>Create</Button>
            </div>
          </form>
        </Card>
      )}

      {orders.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {orders.map(o => (
            <Card key={o.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{o.reference}</div>
                  <div style={{ fontSize: '13px', color: theme.colors.textLight, marginTop: '2px' }}>
                    <Money amount={o.amount} currency={o.currency} /> &middot; {o.frequency}
                  </div>
                  <div style={{ fontSize: '12px', color: theme.colors.textMuted, marginTop: '4px' }}>
                    Next: {o.nextExecutionDate || '-'}
                  </div>
                  <StatusBadge status={o.status} />
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {o.status === 'ACTIVE' && (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => { setActionId(o.id); setActionType('pause'); }}>Pause</Button>
                      <Button variant="ghost" size="sm" onClick={() => { setActionId(o.id); setActionType('cancel'); }}>Cancel</Button>
                    </>
                  )}
                  {o.status === 'PAUSED' && (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => { setActionId(o.id); setActionType('resume'); }}>Resume</Button>
                      <Button variant="ghost" size="sm" onClick={() => { setActionId(o.id); setActionType('cancel'); }}>Cancel</Button>
                    </>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Empty message="No standing orders" />
      )}

      <ConfirmDialog
        open={!!actionId}
        title={`${actionType?.charAt(0).toUpperCase()}${actionType?.slice(1)} standing order`}
        message={actionType === 'cancel' ? 'This standing order will be permanently cancelled.' : `Are you sure you want to ${actionType} this standing order?`}
        confirmLabel={actionType?.charAt(0).toUpperCase() + actionType?.slice(1)}
        danger={actionType === 'cancel'}
        onConfirm={handleAction}
        onCancel={() => { setActionId(null); setActionType(''); }}
      />
    </div>
  );
}
